import {
    GEN1_HEADER_FIELDS,
    GEN1_ORDER_CUSTOMER_FIELDS,
    SYSTEM_ID_FOR_NEW,
    SYSTEM_TYPE_NEW,
    USB_KEY_ITEM_FIELDS,
    USB_VALIDATION_ERROR_KEYS,
} from '../../../../Constants/gen1Constants';

/** Gen1Application state 組み立て（React 非依存）。default 等は orchestrator から deps 注入。 */

function buildCustomerPreserved(prev, customerFieldKeys) {
    const preserved = {};
    for (const key of customerFieldKeys ?? []) {
        if (prev?.gen1Order && key in prev.gen1Order) {
            preserved[key] = prev.gen1Order[key];
        }
    }
    return preserved;
}

function buildTrunkingItemPreservingUsb(prevItem, deps) {
    const defaultItem = deps.getDefaultTrunkingOrderItem();
    const usbPreserveFields = [
        'isNeededUsbKeyAndSoftware',
        ...(deps.usbKeyItemFields ?? []),
    ];
    const usbFromPrev = Object.fromEntries(
        usbPreserveFields.map((k) => [k, prevItem?.[k] ?? defaultItem[k]])
    );
    return {
        ...defaultItem,
        ...usbFromPrev,
        ExchangeRepeaterPairQty: 0,
        RemoveRepeaterQty: 0,
        RepeaterQty: 0,
        MultiSiteNetworkingQty: 0,
    };
}

function buildCommonResetState() {
    return {
        orderNumber: '',
        otherSystemId: '',
        validationErrors: {},
    };
}

/** id / Status など、Trunking リセット後も維持する gen1Order ヘッダメタ */
function buildPreservedOrderHeaderMeta(prev) {
    const meta = {};
    const prevHeaderId = prev.gen1Order?.id;
    if (prevHeaderId != null && prevHeaderId !== 0 && prevHeaderId !== '') {
        meta.id = prevHeaderId;
    }
    if (prev.gen1Order?.Status != null && prev.gen1Order?.Status !== '') {
        meta.Status = prev.gen1Order.Status;
    }
    return meta;
}

function appendPreservedSystemTypeFromPrev(headerFields, prev) {
    if (prev.gen1Order?.SystemType != null) {
        headerFields.SystemType = prev.gen1Order.SystemType;
    }
    if (prev.gen1Order?.SystemId != null) {
        headerFields.SystemId = prev.gen1Order.SystemId;
    }
}

/**
 * Trunking リセット後の gen1Order を組み立てる（Application / System Type 変更共通）。
 * default + Customer + 保持メタ + 空 Trunking + headerOverrides。
 */
function buildGen1OrderAfterTrunkingReset(prev, deps, headerOverrides) {
    const prevItem = prev.gen1Order?.Gen1TrunkingOrderItem ?? null;
    const trunkingItem = buildTrunkingItemPreservingUsb(prevItem, deps);
    const customerPreserved = buildCustomerPreserved(
        prev,
        GEN1_ORDER_CUSTOMER_FIELDS
    );
    const headerMeta = buildPreservedOrderHeaderMeta(prev);

    return {
        ...deps.getDefaultGen1Order(),
        ...customerPreserved,
        ...headerMeta,
        Gen1TrunkingOrderRepeaterSerials: [],
        Gen1TrunkingOrderItem: trunkingItem,
        ...headerOverrides,
    };
}

function buildStateAfterTrunkingReset(
    prev,
    deps,
    { gen1Order, orderForAddRepeater, appStatePatch = {} }
) {
    return {
        ...prev,
        ...appStatePatch,
        ...buildCommonResetState(),
        addRepeater:
            deps.resolveAddRepeaterForApplicationType(orderForAddRepeater),
        gen1Order,
    };
}

/**
 * Application Type 選択 / 変更後の state を構築する。
 * gen1Order は default ベースに Customer と必要ヘッダのみ載せ直す。
 *
 * @param {Object} prev - 変更前の Gen1Application state
 * @param {boolean} includePurchasedRepeaters - 次の申請種別
 * @param {Object} deps
 * @param {() => Object} deps.getDefaultGen1Order
 * @param {() => Object} deps.getDefaultTrunkingOrderItem
 * @param {string[]} deps.usbKeyItemFields
 * @param {(order: any) => boolean} deps.resolveAddRepeaterForApplicationType
 * @returns {Object} 次の state
 */
export function buildStateAfterApplicationTypeChange(
    prev,
    includePurchasedRepeaters,
    deps
) {
    const headerOverrides = {
        IncludePurchasedRepeaters: includePurchasedRepeaters,
    };
    appendPreservedSystemTypeFromPrev(headerOverrides, prev);

    const gen1Order = buildGen1OrderAfterTrunkingReset(
        prev,
        deps,
        headerOverrides
    );
    const orderForAddRepeater = {
        ...prev.gen1Order,
        IncludePurchasedRepeaters: includePurchasedRepeaters,
    };

    return buildStateAfterTrunkingReset(prev, deps, {
        gen1Order,
        orderForAddRepeater,
        appStatePatch: { applicationTypeSelected: true },
    });
}

/**
 * System Type 変更後の state を構築する。
 *
 * @param {Object} prev - 変更前の Gen1Application state
 * @param {string} systemType - 次の SystemType
 * @param {Object} deps
 * @param {() => Object} deps.getDefaultGen1Order
 * @param {() => Object} deps.getDefaultTrunkingOrderItem
 * @param {string[]} deps.usbKeyItemFields
 * @param {(order: any) => boolean} deps.resolveAddRepeaterForApplicationType
 * @returns {Object} 次の state
 */
export function buildStateAfterSystemTypeChange(prev, systemType, deps) {
    const gen1Order = buildGen1OrderAfterTrunkingReset(prev, deps, {
        SystemType: systemType,
        SystemId: systemType === SYSTEM_TYPE_NEW ? SYSTEM_ID_FOR_NEW : '',
        IncludePurchasedRepeaters: prev.gen1Order?.IncludePurchasedRepeaters,
    });
    const orderForAddRepeater = { ...prev.gen1Order, SystemType: systemType };

    return buildStateAfterTrunkingReset(prev, deps, {
        gen1Order,
        orderForAddRepeater,
    });
}

export function buildFormFieldChangeAppStatePatch(prev, deps) {
    const { name, value, checked } = deps;
    let patch = {};
    let validationErrorsPatch = {};

    if (GEN1_HEADER_FIELDS.includes(name)) {
        patch = buildHeaderInputFieldChange(prev, name, value);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch(name);
    }

    if (name === 'isSaveAddressBook') {
        patch = {
            isSaveAddressBook: checked,
        };
    }

    if (name === 'ExchangeRepeater') {
        patch = buildExchangeRepeaterCheckboxChange(prev, checked);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'repeaterOperations',
            'exchangeRepeaterQuantity',
            'otherSystemId',
        ]);
    }

    if (name === 'RemoveRepeater') {
        patch = buildRemoveRepeaterCheckboxChange(prev, checked);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'repeaterOperations',
            'removeRepeaterQuantity',
        ]);
    }

    if (name === 'addRepeater') {
        patch = buildAddRepeaterCheckboxChange(checked);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'addRepeaterOperations',
            'repeaterOperations',
        ]);
    }

    if (name === 'orderNumber') {
        patch = {
            orderNumber: value,
        };
        validationErrorsPatch = buildClearValidationErrorAppStatePatch(name);
    }

    if (name === 'otherSystemId') {
        patch = {
            otherSystemId: digitsOnly(value),
        };
        validationErrorsPatch = buildClearValidationErrorAppStatePatch(name);
    }

    if (name === 'RepeaterQty') {
        patch = buildTrunkingItemQuantityChange(name, value);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'repeaterQty',
            'addRepeaterOperations',
        ]);
    }

    if (name === 'MultiSiteNetworkingQty') {
        patch = buildTrunkingItemQuantityChange(name, value);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'multiSiteNetworkingQty',
            'addRepeaterOperations',
        ]);
    }

    if (name === 'ExchangeRepeaterPairQty') {
        patch = buildTrunkingItemQuantityChange(name, value);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'exchangeRepeaterQuantity',
            'repeaterOperations',
            'otherSystemId',
        ]);
    }

    if (name === 'RemoveRepeaterQty') {
        patch = buildTrunkingItemQuantityChange(name, value);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch([
            'removeRepeaterQuantity',
            'repeaterOperations',
        ]);
    }

    if (name === 'isNeededUsbKeyAndSoftware') {
        patch = {
            trunkingItemPatch: {
                isNeededUsbKeyAndSoftware: checked,
            },
        };
        validationErrorsPatch = buildClearValidationErrorAppStatePatch(
            USB_VALIDATION_ERROR_KEYS
        );
    }

    if (USB_KEY_ITEM_FIELDS.includes(name)) {
        patch = buildTrunkingItemQuantityChange(name, value);
        validationErrorsPatch = buildClearValidationErrorAppStatePatch(
            USB_VALIDATION_ERROR_KEYS
        );
    }

    return {
        ...patch,
        ...validationErrorsPatch,
    };
}

export function buildCustomerAddressBookChangeAppStatePatch(
    prev,
    customerAddressBook,
    address
) {
    let patch = {};

    if (customerAddressBook === -1) {
        patch = {
            gen1Order: {
                ...prev.gen1Order,
                AddressLineOne: '',
                AddressLineTwo: '',
                AddressLineThree: '',
                City: '',
                State: '',
                Zip: '',
                Country: 'US',
                Telephone: '',
            },
        };
    } else if (address) {
        patch = {
            gen1Order: {
                ...prev.gen1Order,
                AddressLineOne: address.AddressLineOne ?? '',
                AddressLineTwo: address.AddressLineTwo ?? '',
                AddressLineThree: address.AddressLineThree ?? '',
                City: address.City ?? '',
                State: address.State ?? '',
                Zip: address.Zip ?? '',
                Country: address.Country ?? 'US',
                Telephone: address.Telephone ?? '',
            },
        };
    }

    const validationErrorsPatch = buildClearValidationErrorAppStatePatch([
        'addressLineOne',
        'addressLineTwo',
        'addressLineThree',
        'city',
        'state',
        'zip',
        'country',
        'telephone',
    ]);
    return {
        ...patch,
        selectedCustomerAddressBookId: customerAddressBook,
        ...validationErrorsPatch,
    };
}

function buildClearValidationErrorAppStatePatch(names) {
    const list = Array.isArray(names) ? names : [names];

    const validationErrorsPatch = {};
    for (const name of list) {
        validationErrorsPatch[lowerFirst(name)] = undefined;
    }
    return {
        validationErrorsPatch,
    };
}

function buildHeaderInputFieldChange(prev, name, value) {
    return {
        gen1Order: {
            ...prev.gen1Order,
            [name]: value,
        },
    };
}

function buildExchangeRepeaterCheckboxChange(prev, checked) {
    return {
        trunkingItemPatch: {
            ExchangeRepeaterPairQty: checked
                ? Math.max(
                      1,
                      prev.gen1Order?.Gen1TrunkingOrderItem
                          ?.ExchangeRepeaterPairQty ?? 0
                  )
                : 0,
        },
    };
}

function buildRemoveRepeaterCheckboxChange(prev, checked) {
    return {
        trunkingItemPatch: {
            RemoveRepeaterQty: checked
                ? Math.max(
                      1,
                      prev.gen1Order?.Gen1TrunkingOrderItem
                          ?.RemoveRepeaterQty ?? 0
                  )
                : 0,
        },
    };
}

function buildAddRepeaterCheckboxChange(checked) {
    if (checked) {
        return {
            addRepeater: true,
        };
    } else {
        return {
            addRepeater: false,
            trunkingItemPatch: {
                RepeaterQty: 0,
                MultiSiteNetworkingQty: 0,
            },
        };
    }
}

function buildTrunkingItemQuantityChange(name, value) {
    let num = Number(value) || 0;
    if (num < 0) num = 0;
    return {
        trunkingItemPatch: {
            [name]: num,
        },
    };
}

function digitsOnly(value) {
    return (value ?? '').toString().replace(/\D/g, '');
}

function lowerFirst(str) {
    if (!str) return str;
    return str[0].toLowerCase() + str.slice(1);
}
